import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Trash2, Check } from 'lucide-react';
import { formatCurrency, formatDate, today, canModifyRecord, isAdminUser, approvalBadge } from '../utils/helpers';

const empty = {
  payment_date: today(),
  payment_type: 'received',
  ledger_id: '',
  reference_type: 'sale',
  reference_id: '',
  amount: 0,
  payment_mode: 'cash',
  bank_id: '',
  bank_name: '',
  cheque_no: '',
  transaction_id: '',
  notes: ''
};

export default function Payments() {
  const [list, setList] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const admin = isAdminUser();

  const load = () => api.get('/payments').then(r => setList(r.data.data || []));
  useEffect(() => {
    load();
    api.get('/ledgers').then(r => setLedgers((r.data.data||[]).filter(l => l.status == 1)));
    api.get('/sales').then(r => setSales((r.data.data||[]).filter(s => s.payment_status !== 'paid')));
    api.get('/purchases').then(r => setPurchases((r.data.data||[]).filter(p => p.payment_status !== 'paid')));
    api.get('/banks').then(r => setBanks((r.data.data||[]).filter(b => b.status == 1)));
  }, []);

  const save = async () => {
    if (!form.amount || form.amount <= 0) return toast.error('Amount required');
    if (form.payment_mode !== 'cash' && !form.bank_id) return toast.error('Select a bank for non-cash payments');
    try {
      const r = await api.post('/payments', form);
      toast.success(r.data.message || 'Payment recorded');
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete payment? Invoice & bank balance will be reverted.')) return;
    try { await api.delete('/payments/' + id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Error'); }
  };

  const approve = async (id) => {
    if (!window.confirm('Approve this payment? Invoice & bank balance will be updated.')) return;
    try { await api.post('/payments/' + id); toast.success('Approved'); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const isReceived = form.payment_type === 'received';
  const filteredLedgers = ledgers.filter(l => isReceived
    ? ['customer','both'].includes(l.party_type)
    : ['supplier','both'].includes(l.party_type));
  const invoices = isReceived
    ? sales.filter(s => s.ledger_id == form.ledger_id)
    : purchases.filter(p => p.ledger_id == form.ledger_id);

  // Aggregates only include approved
  const approved = list.filter(r => r.approval_status === 'approved');
  const received = approved.filter(p => p.payment_type === 'received').reduce((s, r) => s + parseFloat(r.amount||0), 0);
  const paid = approved.filter(p => p.payment_type === 'paid').reduce((s, r) => s + parseFloat(r.amount||0), 0);
  const pendingCount = list.filter(r => r.approval_status === 'pending').length;

  return (
    <div className="card">
      <div className="card-header">
        <h3>Payments ({list.length}) — Received: {formatCurrency(received)} · Paid: {formatCurrency(paid)}{pendingCount > 0 && <span className="badge badge-orange" style={{marginLeft:8,fontSize:11}}>{pendingCount} pending</span>}</h3>
        <button className="btn btn-blue" onClick={() => { setForm(empty); setOpen(true); }}><Plus size={14}/> New Payment</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Payment No</th><th>Date</th><th>Type</th>
              <th>Ledger</th><th>Reference</th><th>Mode</th>
              <th className="td-num">Amount</th><th>Approval</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length ? list.map(p => {
              const badge = approvalBadge(p.approval_status);
              const canMod = canModifyRecord(p);
              const isPending = p.approval_status === 'pending';
              return (
                <tr key={p.id} style={isPending ? {background:'#fffbeb'} : {}}>
                  <td><strong>{p.payment_no}</strong></td>
                  <td>{formatDate(p.payment_date)}</td>
                  <td><span className={'badge ' + (p.payment_type==='received'?'badge-green':'badge-red')}>{p.payment_type}</span></td>
                  <td>{p.ledger_name || '-'}</td>
                  <td>{p.reference_type} {p.reference_id ? '#'+p.reference_id : ''}</td>
                  <td><span className="badge badge-blue">{p.payment_mode}</span></td>
                  <td className="td-num">{formatCurrency(p.amount)}</td>
                  <td>
                    <span className={badge.cls}>{badge.label}</span>
                    {p.created_by_name && <small style={{display:'block',color:'#94a3b8',fontSize:10,marginTop:2}}>by {p.created_by_name}</small>}
                  </td>
                  <td>
                    {admin && isPending && <button className="btn btn-sm btn-green" onClick={()=>approve(p.id)} title="Approve"><Check size={12}/></button>}
                    {' '}
                    {canMod && <button className="btn btn-sm btn-red" onClick={()=>del(p.id)} title="Delete"><Trash2 size={12}/></button>}
                    {!canMod && !isPending && <span style={{color:'#94a3b8',fontSize:11}}>Locked</span>}
                  </td>
                </tr>
              );
            }) : <tr><td colSpan="9" className="empty">No payments</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title="New Payment / Receipt" size="lg"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>Save</button>
        </>}>
        {!admin && (
          <div style={{background:'#fef3c7',border:'1px solid #fcd34d',padding:'10px 12px',borderRadius:8,marginBottom:14,fontSize:13,color:'#92400e'}}>
            Your entry will be saved as <strong>pending</strong> until an admin approves it. Bank balance and invoice settlement happen on approval.
          </div>
        )}
        <div className="form-grid">
          <div className="form-group">
            <label>Payment Type *</label>
            <select value={form.payment_type} onChange={e=>setForm({
              ...form,
              payment_type: e.target.value,
              ledger_id: '',
              reference_id: '',
              reference_type: e.target.value === 'received' ? 'sale' : 'purchase'
            })}>
              <option value="received">Received (from Customer Ledger)</option>
              <option value="paid">Paid (to Supplier Ledger)</option>
            </select>
          </div>
          <div className="form-group">
            <label>Date *</label>
            <input type="date" value={form.payment_date} onChange={e=>setForm({...form, payment_date:e.target.value})}/>
          </div>
          <div className="form-group">
            <label>Ledger</label>
            <select value={form.ledger_id} onChange={e=>setForm({...form, ledger_id:e.target.value, reference_id:''})}>
              <option value="">-- Select Ledger --</option>
              {filteredLedgers.map(l=><option key={l.id} value={l.id}>{l.name}{l.default_vehicle_no ? ` · 🚚 ${l.default_vehicle_no}` : ''}</option>)}
            </select>
            {form.ledger_id && (() => {
              const sel = filteredLedgers.find(l => String(l.id) === String(form.ledger_id));
              return sel?.default_vehicle_no ? (
                <small style={{display:'block',marginTop:4,color:'#0369a1',fontSize:11}}>Default Vehicle: <strong>{sel.default_vehicle_no}</strong></small>
              ) : null;
            })()}
          </div>
          <div className="form-group">
            <label>Against Invoice</label>
            <select value={form.reference_id} onChange={e=>{
              const inv = invoices.find(x => x.id == e.target.value);
              setForm({...form, reference_id: e.target.value, amount: inv ? inv.due_amount : form.amount});
            }}>
              <option value="">-- Advance / General --</option>
              {invoices.map(i=><option key={i.id} value={i.id}>{i.invoice_no} — Due {formatCurrency(i.due_amount)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Amount *</label>
            <input type="number" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})}/>
          </div>
          <div className="form-group">
            <label>Payment Mode</label>
            <select value={form.payment_mode} onChange={e=>setForm({...form, payment_mode:e.target.value, bank_id: e.target.value==='cash'?'':form.bank_id})}>
              <option value="cash">Cash</option>
              <option value="bank">Bank</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>
          {form.payment_mode !== 'cash' && (
            <div className="form-group"><label>Bank Account *</label>
              <select value={form.bank_id} onChange={e=>setForm({...form, bank_id:e.target.value})}>
                <option value="">-- Select Bank --</option>
                {banks.filter(b => b.account_type !== 'cash').map(b=><option key={b.id} value={b.id}>{b.bank_name} — {formatCurrency(b.current_balance)}</option>)}
              </select>
            </div>
          )}
          {form.payment_mode === 'cash' && (
            <div className="form-group"><label>Cash Account</label>
              <select value={form.bank_id} onChange={e=>setForm({...form, bank_id:e.target.value})}>
                <option value="">-- Select (optional) --</option>
                {banks.filter(b => b.account_type === 'cash').map(b=><option key={b.id} value={b.id}>{b.bank_name} — {formatCurrency(b.current_balance)}</option>)}
              </select>
            </div>
          )}
          {form.payment_mode === 'cheque' && <div className="form-group"><label>Cheque No</label><input value={form.cheque_no} onChange={e=>setForm({...form, cheque_no:e.target.value})}/></div>}
          {(form.payment_mode === 'upi' || form.payment_mode === 'bank') && <div className="form-group"><label>Transaction ID</label><input value={form.transaction_id} onChange={e=>setForm({...form, transaction_id:e.target.value})}/></div>}
          <div className="form-group full-width"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
        </div>
      </Modal>
    </div>
  );
}
